import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NetworkConstants, appAccessOptionsUserRolesData, deepCopy, filtersOptionsUserRolesData, rightsOptionsUserRolesData } from 'diwimamodelweb/lib';
import { SettingsService } from '../settings.service';
import { HuntingDistrictService } from '../hunting-district.service';
import { BehaviorSubject, firstValueFrom, skipWhile, take } from 'rxjs';

@Component({
  selector: 'app-user-roles-data-access-dialog',
  templateUrl: './user-roles-data-access-dialog.component.html',
  styleUrl: './user-roles-data-access-dialog.component.css'
})
export class UserRolesDataAccessDialogComponent {
  dataAccessForm!: FormGroup
  collectionList = [
    {
      col: NetworkConstants.COLLECTION_GAMERECORD,
      filters: true,
      title: 'COCKPIT.OBJECTS.GameRecord'
    },
    {
      col: NetworkConstants.COLLECTION_POI,
      filters: true,
      title: 'COCKPIT.OBJECTS.PointOfInterest'
    },
    {
      col: NetworkConstants.COLLECTION_WORK_ORDER,
      filters: true,
      title: 'COCKPIT.OBJECTS.WorkOrder'
    },
    {
      col: NetworkConstants.COLLECTION_HUNTING_DISTRICT,
      filters: true,
      title: 'COCKPIT.OBJECTS.HuntingDistrict'
    },
    {
      col: NetworkConstants.COLLECTION_LABSAMPLE, 
      filters: true,
      title: 'COCKPIT.OBJECTS.LaboratorySample'
    },
    {
      col: NetworkConstants.COLLECTION_PERSON,
      filters: false,
      title: 'COCKPIT.OBJECTS.Person'
    },
    {
      col: NetworkConstants.COLLECTION_USER, 
      filters: true,
      title: 'COCKPIT.OBJECTS.User'
    },
    {
      col: NetworkConstants.COLLECTION_CTX_SETTINGS, 
      filters: false,
      title: 'COCKPIT.TAB_NAV.CONTEXT_SETTINGS'
    }
  ]
  rightsList = rightsOptionsUserRolesData
  private readonly baseFiltersList = filtersOptionsUserRolesData
  filtersList: any[] = filtersOptionsUserRolesData
  dataAccessValues = []

  filtersFilterCtrl = new FormControl<string>('', { nonNullable: true })
  filteredFiltersList$ = new BehaviorSubject<any[]>([])

  constructor(
    private fb:FormBuilder,
    public dialogRef: MatDialogRef<UserRolesDataAccessDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private settingsService: SettingsService,
    private huntingDistrictService: HuntingDistrictService,
  ) {
    dialogRef.disableClose = true
  }

  async ngOnInit(): Promise<void> {
    // Built before the await
    this.dataAccessForm = this.fb.group({
      collection: [ , Validators.required],
      rights: [ ,Validators.required],
      filters: [[]]
    })

    const context = await firstValueFrom(this.settingsService.selectedUserContext$.pipe(
      skipWhile(el => !el), take(1)
    ))

    this.dataAccessValues = deepCopy(this.data.data.dataAccessInfo) ?? []

    if(this.data.data?.values){
      const {collection, rights, filters} = this.data.data.values

      if (collection === NetworkConstants.COLLECTION_POI) {
        this.addCategoriesToFilters(context)
      } else if (collection === NetworkConstants.COLLECTION_HUNTING_DISTRICT) {
        this.addHuntingDistrictsToFilters()
      }
      this.dataAccessForm.patchValue({collection, rights, filters: filters ?? []})
    }
    this.refreshFilteredFiltersList()

    this.dataAccessForm.get('collection')?.valueChanges.subscribe( value => {
      if (value === NetworkConstants.COLLECTION_POI) {
        this.addCategoriesToFilters(context)
      } else if (value === NetworkConstants.COLLECTION_HUNTING_DISTRICT) {
        this.addHuntingDistrictsToFilters()
      } else {
        this.filtersList = this.baseFiltersList
      }
      this.dataAccessForm.get('rights')?.setValue([])
      this.dataAccessForm.get('filters')?.setValue([])
      this.filtersFilterCtrl.setValue('', { emitEvent: false })
      this.refreshFilteredFiltersList()
    })

    this.filtersFilterCtrl.valueChanges.subscribe(() => this.refreshFilteredFiltersList())
  }

  checkExistingCollection(col) {
    return this.dataAccessValues?.find( d => d.collection === col)
  }

  addCategoriesToFilters(context) {
    const poi_cat = context?.settingsMap?.get(NetworkConstants.SETTING_GEO_CATEGORY)?.values ?? []
    const poiFilter = poi_cat.map( p => { return {type: p.identifier, only: [NetworkConstants.COLLECTION_POI], title: p.title, translate: undefined}})
    this.filtersList = [...this.baseFiltersList, ...poiFilter]
  }

  /** Individual hunting districts, selectable as data-access filters for the HuntingDistrict row. */
  addHuntingDistrictsToFilters(): void {
    const buildFrom = (districts: any[]) => {
      const hdFilters = (districts ?? []).map(d => ({
        type: d.identifier,
        only: [NetworkConstants.COLLECTION_HUNTING_DISTRICT],
        title: d.getListItemTitle ? d.getListItemTitle() : d.identifier,
        translate: undefined,
      }))
      this.filtersList = [...this.baseFiltersList, ...hdFilters]
      this.refreshFilteredFiltersList()
    }

    const current = this.huntingDistrictService.huntindDistrictSubject$.value
    if (current) { buildFrom(current); return }

    this.huntingDistrictService.huntindDistrictSubject$
      .pipe(skipWhile(el => !el), take(1))
      .subscribe(buildFrom)
  }

  private refreshFilteredFiltersList(): void {
    const collection = this.dataAccessForm?.value?.collection
    const needle = (this.filtersFilterCtrl.value ?? '').trim().toLowerCase()
    const scoped = this.filtersList.filter(f => f?.only?.includes(collection))
    this.filteredFiltersList$.next(
      needle
        ? scoped.filter(f => (f.title ?? f.translate ?? '').toString().toLowerCase().includes(needle))
        : scoped,
    )
  }

  onCancel(){
    this.dialogRef.close()
  }

  onConfirm() {
    const { collection, rights, filters } = this.dataAccessForm.value
    const selected: string[] = (filters ?? []).filter(Boolean)
    const data: any = { collection, rights }
    if (selected.length) data.filters = selected

    this.data.positiveCallback(data)
    this.dialogRef.close(data)
  }
}
